// R16 — THE WARM PROMPT FOR I6 RULE 3, out here where a gate can CALL it.
//
// The server refuses a silent duplicate and hands back the person the member already has. This is
// what she sees. Never a scold, never a dead end: she is told she already has this person and given
// ALL THREE ways out. Click-outside does nothing — C2's law for anything that would lose work —
// and the X and Cancel both mean cancel.
//
// The three answers are `src/personDuplicate.js`, shared with the server, because a choice string
// the server does not recognise is not an error there: it reads as "she has not answered yet", the
// prompt reopens, and her tap on "Keep both" does nothing at all, forever.
import { RESOLUTION_REPLACE, RESOLUTION_KEEP_BOTH, RESOLUTION_CANCEL } from "../personDuplicate.js";

// A saved Person of Interest, as every subject picker on the site holds one. It lives here because
// this component is the thing a gate loads; App.tsx imports the type back.
export type SubjectPickerPerson = {
  id: string; name: string; firstName?: string; middleName?: string; lastName?: string;
  relationship?: string; birthDate?: string; birthTime?: string; birthPeriod?: string;
  birthTimeUnknown?: boolean; birthplace?: string; birthRegion?: string; birthCountry?: string;
  latitude?: string; longitude?: string; timezone?: string;
};

// WRITTEN AS LITERALS ON PURPOSE, and the fixture holds them to the law. `typeof RESOLUTION_REPLACE`
// would read as `any` here — the law is untyped JS because the SERVER has to load it too — and a
// union that is secretly `any` accepts the typo it exists to catch. So the strings are spelled out
// for the compiler, and `scripts/r16-duplicate-person.mjs` checks this line against the imported
// values rather than trusting them to stay in step.
export type DuplicateChoice = "replace" | "keep-both" | "cancel";

export function DuplicatePersonPrompt({ existing, onChoose }: {
  existing: SubjectPickerPerson;
  onChoose: (choice: DuplicateChoice) => void;
}) {
  // No birth date means no "born …" clause: rendering it anyway prints "Someone, born ," at her.
  const when = existing.birthDate ? new Date(`${existing.birthDate}T12:00:00`).toLocaleDateString(undefined, { month: "long", day: "numeric", year: "numeric" }) : "";
  return (
    <div className="modal-backdrop duplicate-prompt__backdrop">
      <div className="reading-modal duplicate-prompt" role="dialog" aria-modal="true">
        <button className="modal-close" type="button" onClick={() => onChoose(RESOLUTION_CANCEL)} aria-label="Cancel">x</button>
        <p className="eyebrow">Already in your circle</p>
        <h3>You already have {existing.name}</h3>
        <p className="duplicate-prompt__body">
          {existing.name}{when ? `, born ${when},` : ""} is already saved to your people of interest.
          Would you like to update what you have, or keep them both?
        </p>
        <div className="duplicate-prompt__choices">
          <button type="button" className="button button--primary" onClick={() => onChoose(RESOLUTION_REPLACE)}>Update the one I have</button>
          <button type="button" className="button button--ghost" onClick={() => onChoose(RESOLUTION_KEEP_BOTH)}>Keep both</button>
          <button type="button" className="button button--ghost" onClick={() => onChoose(RESOLUTION_CANCEL)}>Cancel</button>
        </div>
      </div>
    </div>
  );
}
